import { GOOGLE_EXPIRED, GOOGLE_PLACEHOLDER } from '../../../shared/placesRetention';
import type { RouteStop } from '../../../shared/types';
import type { BusinessType, DetectedPos } from '../../../shared/enums';
import { COMPETITOR_POS } from '../../../shared/enums';
import type { CorridorPlace } from '../../../shared/types';

/** Pure logic for the Route tab (M2): distances, greedy ordering, pin classification. */

const EARTH_RADIUS_M = 6_371_000;
const rad = (deg: number) => (deg * Math.PI) / 180;

export const haversineMeters = (aLat: number, aLng: number, bLat: number, bLng: number): number => {
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
};

export interface LatLng {
  lat: number;
  lng: number;
}

export interface OrderedStop<T extends LatLng> {
  stop: T;
  /** Meters from the previous point (anchor for the first stop). */
  legMeters: number;
}

/** The stops as given, with each leg's distance: a planned route keeps the planner's order (D83). */
export const keepOrder = <T extends LatLng>(anchor: LatLng, stops: readonly T[]): OrderedStop<T>[] =>
  stops.map((stop, i) => {
    const prev = i === 0 ? anchor : (stops[i - 1] as T);
    return { stop, legMeters: haversineMeters(prev.lat, prev.lng, stop.lat, stop.lng) };
  });

/** Greedy nearest-neighbour from the anchor (D26): good enough for 10–15 stops, fully offline. */
export const orderStops = <T extends LatLng>(anchor: LatLng, stops: readonly T[]): OrderedStop<T>[] => {
  const remaining = [...stops];
  const ordered: OrderedStop<T>[] = [];
  let cursor: LatLng = anchor;
  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const s = remaining[i] as T;
      const d = haversineMeters(cursor.lat, cursor.lng, s.lat, s.lng);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const [next] = remaining.splice(bestIdx, 1);
    ordered.push({ stop: next as T, legMeters: bestDist });
    cursor = next as T;
  }
  return ordered;
};

/** "300 ft" under ~0.095 mi, else "0.4 mi" — the units a US field rep thinks in. */
export const formatDistance = (meters: number): string => {
  const miles = meters / 1609.344;
  if (miles < 0.095) return `${Math.max(50, Math.round((meters * 3.28084) / 50) * 50)} ft`;
  return `${miles.toFixed(1)} mi`;
};

// ---------------------------------------------------------------- pin classification (D25)

export type PinKind = 'competitor' | 'prospect' | 'in_pipeline' | 'hidden_square' | 'hidden_lost';

export const classifyPlace = (p: CorridorPlace): PinKind => {
  if (p.lead_id) return p.stage === 'closed_lost' ? 'hidden_lost' : 'in_pipeline';
  if (p.detected_pos === 'square') return 'hidden_square';
  if (COMPETITOR_POS.includes(p.detected_pos as DetectedPos)) return 'competitor';
  return 'prospect'; // unknown or pending — still worth a knock
};

export const isHidden = (kind: PinKind): boolean => kind === 'hidden_square' || kind === 'hidden_lost';

/** The corridor cross-references leads by place_id, so a hand-entered lead (no place_id) is unknown
 *  to it even when the stored stop or the current pick already points at that lead: a fresh copy of
 *  the place keeps the link an earlier copy carried. */
export const keepLeadLink = <T extends { lead_id?: string; stage?: CorridorPlace['stage'] }>(
  p: T,
  prev?: { lead_id?: string; stage?: CorridorPlace['stage'] },
): T =>
  p.lead_id || !prev?.lead_id
    ? p
    : { ...p, lead_id: prev.lead_id, ...(prev.stage ? { stage: prev.stage } : {}) };

/** The lead a route stop's visit belongs to: the one the corridor linked, else a lead on this phone
 *  already on that Google place (created after the scout, or on an earlier route), else none. The
 *  API allows one lead per place, so creating a second one would be rejected. */
export const leadIdForStop = (
  stop: { place_id: string; lead_id?: string },
  leads: readonly { id: string; place_id: string | null }[],
): string | undefined => {
  if (stop.lead_id && leads.some((l) => l.id === stop.lead_id)) return stop.lead_id;
  return leads.find((l) => l.place_id === stop.place_id)?.id;
};

/** Google place types → our business_type enum, first match wins. */
export const inferBusinessType = (types: readonly string[]): BusinessType => {
  const set = new Set(types);
  if (set.has('pizza_restaurant')) return 'pizzeria';
  if (set.has('bar') || set.has('pub') || set.has('night_club')) return 'bar';
  if (set.has('cafe') || set.has('coffee_shop') || set.has('bakery') || set.has('tea_house')) return 'cafe';
  if (set.has('hair_salon') || set.has('beauty_salon') || set.has('nail_salon') || set.has('barber_shop'))
    return 'salon';
  if (
    set.has('restaurant') ||
    set.has('meal_takeaway') ||
    set.has('meal_delivery') ||
    [...set].some((t) => t.endsWith('_restaurant'))
  ) {
    return 'restaurant';
  }
  if (
    [...set].some(
      (t) => t === 'store' || t.endsWith('_store') || t === 'shopping_mall' || t === 'supermarket',
    )
  ) {
    return 'retail';
  }
  return 'other';
};

/** Only proven independent route content may become a durable lead. */
export const canAutoCreateLead = (stop: Pick<RouteStop, 'source' | 'name'>): boolean =>
  ['roster', 'lead', 'signal'].includes(stop.source ?? '') &&
  stop.name !== GOOGLE_PLACEHOLDER && stop.name !== GOOGLE_EXPIRED;
